import { useCallback, useMemo, useRef } from 'react'

import { useEvents } from '../hooks/useEvents'
import { useSelector } from 'react-redux'

import { Box, Flex } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { throttle } from 'lodash'

import { Spinner } from '@/components/ui'
import { selectIncidentTraceLogDrawerState } from '@/redux/ui/uiSlice'

import { EventFiltersI } from '../types/types'
import {
  filterEventsForIncidentTab,
  getResolvedActionEventRefs,
  isHighlightedFromResolution,
  matchesResolvedActionEventRef,
} from '../utils/utils'
import { EventItem } from './EventItem'
import { TRACE_LOG_PANEL_HEIGHT } from './TraceLogContent'

const onEndReachedThreshold = 0.95

interface EventFeedPropsI extends EventFiltersI {
  showOnlyIncidentRelatedEvents?: boolean
}

export const EventFeed = ({
  incident,
  filters,
  showOnlyIncidentRelatedEvents = false,
}: EventFeedPropsI) => {
  const ref = useRef<HTMLDivElement>(null)
  const { highlightedEvent } = useSelector(selectIncidentTraceLogDrawerState)

  // For the Incident tab (showOnlyIncidentRelatedEvents=true), don't center on
  // highlightedEvent - use incident.detectedAt so all related events are fetched.
  // For Device/Facility tabs, center on highlightedEvent if user clicked an EVENT_REF.
  const centerTimestamp = showOnlyIncidentRelatedEvents
    ? undefined
    : highlightedEvent?.detectedAt

  const { isLoading, events, getNewerEvents, getOlderEvents, hasOlder } =
    useEvents({
      incident,
      filters,
      centerTimestamp,
    })

  const onScroll = useCallback(
    throttle(async () => {
      if (ref?.current) {
        const { scrollTop, scrollHeight, clientHeight } = ref.current

        // Scrolled to the top
        if (scrollTop <= 0) {
          const hasMoreEvents = await getNewerEvents()
          if (hasMoreEvents) {
            ref?.current?.scrollTo({ top: 50, behavior: 'auto' })
          }
        }
        // Scrolled to the bottom
        const scrollPosition = scrollTop + clientHeight
        const endScrollThreshold = scrollHeight * onEndReachedThreshold
        if (scrollPosition >= endScrollThreshold && hasOlder) {
          await getOlderEvents()
        }
      }
    }, 200),
    [getNewerEvents]
  )

  const eventExternalIdMatchesIncident = (eventExternalId: string) =>
    incident?.eventExternalIds?.includes(eventExternalId) ?? false

  const resolvedActionEventRefs = useMemo(
    () => getResolvedActionEventRefs(incident?.actions),
    [incident?.actions]
  )

  const filteredEvents = useMemo(
    () =>
      filterEventsForIncidentTab(
        events,
        showOnlyIncidentRelatedEvents,
        incident?.eventExternalIds,
        resolvedActionEventRefs
      ),
    [
      events,
      showOnlyIncidentRelatedEvents,
      incident?.eventExternalIds,
      resolvedActionEventRefs,
    ]
  )

  return (
    <Flex
      borderTop='1px solid'
      borderTopColor='border.200'
      direction='column'
      gap='8px'
      height={TRACE_LOG_PANEL_HEIGHT}
      onScroll={onScroll}
      overflowX='hidden'
      overflowY='auto'
      paddingInline='24px'
      paddingTop='24px'
      ref={ref}
    >
      <AnimatePresence>
        {filteredEvents?.map((e) => (
          <motion.div
            animate={{ x: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            initial={{ x: -200, opacity: 0 }}
            key={e.node.externalId}
          >
            <EventItem
              event={e.node}
              isCauseOfIncident={eventExternalIdMatchesIncident(
                e.node.externalId
              )}
              isFromResolvedAction={matchesResolvedActionEventRef(
                e.node,
                resolvedActionEventRefs
              )}
              isHighlightedFromResolution={isHighlightedFromResolution(
                e.node,
                highlightedEvent
              )}
              key={e.node?.externalId}
              shouldShowDeviceInfo={Boolean(filters?.facilityId)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
      {isLoading && (
        <Box
          alignContent='center'
          backgroundColor='white'
          borderColor='border.200'
          borderRadius='10px'
          borderWidth='1px'
          height='80px'
          left='calc(50% - 40px)'
          pointerEvents='none'
          position='absolute'
          top='calc(50% - 40px)'
          width='80px'
        >
          <Spinner size='lg' />
        </Box>
      )}
    </Flex>
  )
}
